/**
 * R268q · Feb 2026 — Organization Billing Profile + Template Setup.
 *
 * Configure once, every quote/invoice/credit note auto-fills your legal
 * name, GSTIN, bank details, logo, and applies your chosen colour +
 * layout preset. Doc-type-specific header/footer/terms live here too.
 */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Save, Building2, Palette, FileText, Landmark, Plus, Trash2, Star } from "lucide-react";
import DocumentPreview, { samplePreviewDoc } from "@/components/DocumentPreview";

const LAYOUTS = [
  { key: "classic",  label: "Classic",  swatch: "#111827" },
  { key: "modern",   label: "Modern",   swatch: "#4F46E5" },
  { key: "minimal",  label: "Minimal",  swatch: "#000000" },
  { key: "bordered", label: "Bordered", swatch: "#166534" },
];

export default function BillingSetup() {
  const { activeWorkspaceId: ws } = useAuth();
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  // R268ll · Feb 2026 — Controlled tab so we can lazy-render the heavy
  // DocumentPreview only when the Template tab is actually visible.
  const [activeTab, setActiveTab] = useState("org");
  // R268ll · Feb 2026 — Explicit loading + error state so the page never
  // sits on a silent blank screen when auth is still hydrating or the
  // API errored. Retry is one tap.
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!ws) return;                       // wait for auth hydration
    setLoading(true); setError(null);
    try {
      const r = await api.get(`/gst/workspaces/${ws}/billing-profile`);
      setProfile(r.data);
    } catch (e) {
      setError(apiErrorMessage(e));
      toast.error(apiErrorMessage(e));
    } finally { setLoading(false); }
  }, [ws]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { _new, layout_presets, ...payload } = profile;  // eslint-disable-line no-unused-vars
      const r = await api.patch(`/gst/workspaces/${ws}/billing-profile`, payload);
      setProfile({ ...r.data, layout_presets: profile.layout_presets });
      toast.success("Billing profile saved");
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  if (!ws || loading) {
    return (
      <div className="p-6 space-y-3" data-testid="billing-setup-loading">
        <div className="h-6 w-56 bg-muted rounded animate-pulse" />
        <div className="h-4 w-80 bg-muted/70 rounded animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-6 max-w-2xl">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-1">
              <div className="h-3 w-24 bg-muted/70 rounded animate-pulse" />
              <div className="h-10 bg-muted/50 rounded animate-pulse" />
            </div>
          ))}
        </div>
        {!ws && (
          <div className="text-xs text-muted-foreground mt-4">Waiting for workspace…</div>
        )}
      </div>
    );
  }
  if (error && !profile) {
    return (
      <div className="p-6 space-y-3" data-testid="billing-setup-error">
        <div className="text-sm text-rose-600">Could not load billing profile.</div>
        <div className="text-xs text-muted-foreground">{error}</div>
        <Button size="sm" onClick={load} data-testid="billing-setup-retry">Retry</Button>
      </div>
    );
  }
  if (!profile) {
    return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <>
      <PageHeader
        eyebrow="Settings · Billing"
        title="Organization billing profile"
        description="One place to set everything that auto-fills your quotes, invoices, and credit notes."
        testid="billing-setup-header"
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 max-w-6xl">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="rounded-none">
            <TabsTrigger value="org" data-testid="bp-tab-org">
              <Building2 className="w-4 h-4 mr-1" /> Organisation
            </TabsTrigger>
            <TabsTrigger value="template" data-testid="bp-tab-template">
              <Palette className="w-4 h-4 mr-1" /> Template & Colours
            </TabsTrigger>
            <TabsTrigger value="text" data-testid="bp-tab-text">
              <FileText className="w-4 h-4 mr-1" /> Doc Text
            </TabsTrigger>
          </TabsList>

          {/* ─── Organisation ── */}
          <TabsContent value="org" className="space-y-3">
            {/* R268v · Feb 2026 — GST registration toggle. Non-GST
                workspaces get GST-specific fields hidden across the
                composer, preview and printable PDF. */}
            <label className={
              "flex items-start gap-3 p-3 rounded border cursor-pointer " +
              (profile.gst_registered === false
                ? "border-slate-300 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/40"
                : "border-primary/40 bg-primary/5")
            } data-testid="bp-gst-registered-wrap">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4"
                data-testid="bp-gst_registered"
                checked={profile.gst_registered !== false}
                onChange={(e) => set("gst_registered", e.target.checked)}
              />
              <div className="flex-1">
                <div className="font-semibold text-sm">
                  This workspace is GST-registered
                </div>
                <div className="text-xs text-muted-foreground">
                  When on, invoices show GSTIN / HSN / CGST-SGST-IGST split, TDS, place of supply and Tax Invoice titles. Turn off if you're below the GST threshold, a freelancer, or invoice foreign customers.
                </div>
              </div>
            </label>
            {/* R268w · Home currency of the workspace. Every doc has its
                own currency + FX rate to this, so aggregate reports can
                be summed in one currency. */}
            <div>
              <Label className="text-xs">Home currency</Label>
              <Select value={profile.home_currency || "INR"}
                        onValueChange={(v) => set("home_currency", v)}>
                <SelectTrigger className="h-10 rounded-none" data-testid="bp-home_currency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD", "CAD", "JPY"].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="text-[11px] text-muted-foreground mt-1">
                All aggregate reports (revenue, ITC ledger, GSTR) are rolled up in this currency. Individual invoices can still be raised in any currency.
              </div>
            </div>
            {[
              ["legal_name", "Legal name *", "e.g. Acquilix Technologies Pvt Ltd", true],
              ["trade_name", "Trade / brand name", "e.g. Acquilix", true],
              ["gstin", "GSTIN", "27AAPFU0939F1ZV", "gst"],
              ["pan", "PAN", "AAPFU0939F", true],
              ["cin", "CIN (optional)", "U74999MH2020PTC340...=", true],
              ["state_code", "State code", "27", "gst"],
              ["email", "Billing email", "billing@acquilix.io", true],
              ["phone", "Phone", "+91 98...", true],
              ["website", "Website", "https://acquilix.io", true],
            ].filter(([, , , show]) => show === true || (show === "gst" && profile.gst_registered !== false)).map(([k, label, ph]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                <Input value={profile[k] || ""} onChange={(e) => set(k, e.target.value)}
                          placeholder={ph} className="h-10 rounded-none"
                          data-testid={`bp-${k}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs">Registered address</Label>
              <Textarea value={profile.address || ""} rows={2}
                          onChange={(e) => set("address", e.target.value)}
                          className="rounded-none" data-testid="bp-address" />
            </div>
            <div className="pt-3 border-t border-border">
              {/* Feb 2026 R293zza · Multi-bank editor so authors can
                  keep one INR account + one or more international
                  (USD / EUR / GBP) correspondent accounts. The entry
                  flagged `is_default` is used when no currency
                  match is found; a currency match wins over default. */}
              <BankAccountsEditor
                accounts={profile.bank_accounts || []}
                homeCurrency={profile.home_currency || "INR"}
                onChange={(next) => set("bank_accounts", next)}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-3 border-t border-border">
              <div>
                <Label className="text-xs">Logo URL</Label>
                <Input value={profile.logo_url || ""}
                          onChange={(e) => set("logo_url", e.target.value)}
                          placeholder="https://…/logo.png" className="h-10 rounded-none"
                          data-testid="bp-logo_url" />
              </div>
              <div>
                <Label className="text-xs">Signature URL</Label>
                <Input value={profile.signature_url || ""}
                          onChange={(e) => set("signature_url", e.target.value)}
                          placeholder="https://…/sig.png" className="h-10 rounded-none"
                          data-testid="bp-signature_url" />
              </div>
            </div>
          </TabsContent>

          {/* ─── Template & Colours ── */}
          <TabsContent value="template" className="space-y-0">
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-6">
              <div className="space-y-4">
                <div>
                  <Label className="text-xs">Layout preset</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {LAYOUTS.map((l) => (
                      <button
                        key={l.key}
                        onClick={() => set("layout_preset", l.key)}
                        data-testid={`bp-layout-${l.key}`}
                        className={
                          "border p-3 text-left space-y-1 transition " +
                          (profile.layout_preset === l.key
                            ? "border-primary ring-2 ring-primary/30 bg-primary/5"
                            : "border-border hover:border-foreground/40")
                        }>
                        <div className="w-full h-8" style={{ background: l.swatch }} />
                        <div className="text-xs font-semibold">{l.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label className="text-xs">Primary colour</Label>
                    <Input type="color" value={profile.primary_color || "#111827"}
                              onChange={(e) => set("primary_color", e.target.value)}
                              className="h-10 rounded-none w-full"
                              data-testid="bp-primary_color" />
                  </div>
                  <div>
                    <Label className="text-xs">Secondary colour</Label>
                    <Input type="color" value={profile.secondary_color || "#6B7280"}
                              onChange={(e) => set("secondary_color", e.target.value)}
                              className="h-10 rounded-none w-full"
                              data-testid="bp-secondary_color" />
                  </div>
                  <div>
                    <Label className="text-xs">Font family</Label>
                    <Select value={profile.font_family || "Inter"}
                              onValueChange={(v) => set("font_family", v)}>
                      <SelectTrigger className="h-10 rounded-none" data-testid="bp-font_family">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["Inter", "Roboto", "Helvetica", "Times", "Georgia"].map((f) => (
                          <SelectItem key={f} value={f}>{f}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* R268s · Live preview mirrors the PDF template as the
                  user tweaks. Sample invoice keeps the preview useful
                  even for empty accounts. R268ll: lazy-mounted so it
                  never blocks the Organisation tab from rendering. */}
              <div className="space-y-2" data-testid="bp-live-preview">
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground">Live preview</div>
                <div className="rounded border border-border bg-muted/30 p-3 max-h-[560px] overflow-y-auto">
                  {activeTab === "template" ? (
                    <DocumentPreview doc={samplePreviewDoc()} profile={profile} compact />
                  ) : (
                    <div className="h-40 flex items-center justify-center text-xs text-muted-foreground">
                      Preview mounts when this tab is active.
                    </div>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground">
                  Sample data · matches the exported PDF 1:1.
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ─── Doc-specific text ── */}
          <TabsContent value="text" className="space-y-3">
            {[
              { kind: "quote",       label: "Quotation",        supportsBrand: true },
              { kind: "invoice",     label: "Invoice",          supportsBrand: true },
              { kind: "credit_note", label: "Credit note",      supportsBrand: false },
            ].map(({ kind, label, supportsBrand }) => (
              <div key={kind} className="border border-border p-3 space-y-2">
                <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
                  {label}
                </div>
                <div>
                  <Label className="text-xs">Header</Label>
                  <Input value={profile[`${kind}_header`] || ""}
                            onChange={(e) => set(`${kind}_header`, e.target.value)}
                            placeholder="e.g. Delivery in 15 days"
                            className="h-10 rounded-none"
                            data-testid={`bp-${kind}_header`} />
                </div>
                <div>
                  <Label className="text-xs">Terms</Label>
                  <Textarea value={profile[`${kind}_terms`] || ""} rows={2}
                                onChange={(e) => set(`${kind}_terms`, e.target.value)}
                                className="rounded-none"
                                data-testid={`bp-${kind}_terms`} />
                </div>
                <div>
                  <Label className="text-xs">Footer</Label>
                  <Input value={profile[`${kind}_footer`] || ""}
                            onChange={(e) => set(`${kind}_footer`, e.target.value)}
                            placeholder="Thank you for your business"
                            className="h-10 rounded-none"
                            data-testid={`bp-${kind}_footer`} />
                </div>
                {/* Feb 2026 R293w · Branded-PDF package controls per doc
                    type. Cover page + optional Scope / Details page. */}
                {supportsBrand && (
                <div className="border-t border-dashed border-border pt-2 mt-2 space-y-2">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-primary">
                    Branded PDF package
                  </div>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input type="checkbox"
                              className="h-4 w-4 cursor-pointer"
                              checked={!!profile[`${kind}_cover_enabled`]}
                              onChange={(e) => set(`${kind}_cover_enabled`, e.target.checked)}
                              data-testid={`bp-${kind}_cover_enabled`} />
                    <span>Include a cover page on every {label.toLowerCase()}</span>
                  </label>
                  {profile[`${kind}_cover_enabled`] && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pl-6">
                      <div>
                        <Label className="text-xs">Cover tagline</Label>
                        <Input value={profile[`${kind}_cover_tagline`] || ""}
                                  onChange={(e) => set(`${kind}_cover_tagline`, e.target.value)}
                                  placeholder="A proposal, prepared with care"
                                  className="h-10 rounded-none"
                                  data-testid={`bp-${kind}_cover_tagline`} />
                      </div>
                      <div>
                        <Label className="text-xs">Accent colour</Label>
                        <div className="flex items-center gap-2">
                          <input type="color"
                                    value={profile[`${kind}_cover_accent`] || "#111827"}
                                    onChange={(e) => set(`${kind}_cover_accent`, e.target.value)}
                                    className="h-10 w-14 border border-border rounded-none cursor-pointer p-0"
                                    data-testid={`bp-${kind}_cover_accent`} />
                          <Input value={profile[`${kind}_cover_accent`] || "#111827"}
                                    onChange={(e) => set(`${kind}_cover_accent`, e.target.value)}
                                    className="h-10 rounded-none font-mono text-xs flex-1" />
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Scope / details (optional, prints on its own page)</Label>
                    <Textarea value={profile[`${kind}_details`] || ""} rows={4}
                                  onChange={(e) => set(`${kind}_details`, e.target.value)}
                                  placeholder={"Scope of work\n\n- What's included\n- Deliverables\n- Timelines\n\nAssumptions\n\n- Content provided by client\n- Review cycles capped at 2"}
                                  className="rounded-none font-mono text-xs"
                                  data-testid={`bp-${kind}_details`} />
                    <div className="text-[10px] text-muted-foreground mt-1">
                      Blank lines create paragraph breaks. Lines starting with <code>-</code> become bullets.
                    </div>
                  </div>
                </div>
                )}
              </div>
            ))}
          </TabsContent>
        </Tabs>

        <div className="mt-6 sticky bottom-4">
          <Button size="lg" onClick={save} disabled={saving}
                    className="rounded-none h-11 w-full sm:w-auto"
                    data-testid="bp-save">
            <Save className="w-4 h-4 mr-1" />
            {saving ? "Saving…" : "Save billing profile"}
          </Button>
        </div>
      </div>
    </>
  );
}


/* ─── Feb 2026 R293zza · Multi-bank-accounts editor ───────────────── */
// Lets a workspace list every bank account the business receives money
// into. Currency on each row drives the auto-pick on a document (the
// PDF renderer prefers the row whose `currency` matches the doc). One
// row is flagged default — used when nothing matches.
function BankAccountsEditor({ accounts, homeCurrency, onChange }) {
  const rows = Array.isArray(accounts) ? accounts : [];

  const update = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const setDefault = (idx) => {
    const next = rows.map((r, i) => ({ ...r, is_default: i === idx }));
    onChange(next);
  };
  const remove = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    // If we just dropped the default and others remain, promote the first.
    if (next.length && !next.some((r) => r.is_default)) {
      next[0] = { ...next[0], is_default: true };
    }
    onChange(next);
  };
  const add = () => {
    const seedCurrency = rows.length === 0 ? homeCurrency : "USD";
    const next = [
      ...rows,
      {
        label: rows.length === 0 ? "Primary account" : "International account",
        currency: seedCurrency,
        bank_name: "",
        account_name: "",
        account_number: "",
        ifsc: "",
        swift: "",
        iban: "",
        branch: "",
        bank_address: "",
        upi_id: "",
        // Feb 2026 R293zze · intl-wire fields
        payment_method: "",
        ach_routing: "",
        fedwire_routing: "",
        beneficiary_address: "",
        default_for_currency: [],
        is_default: rows.length === 0,
      },
    ];
    onChange(next);
  };

  return (
    <div className="space-y-3" data-testid="bp-bank-accounts">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1.5">
            <Landmark className="w-3.5 h-3.5" /> Bank accounts
          </div>
          <div className="text-[11px] text-muted-foreground mt-0.5">
            Add every account you receive money into — the PDF auto-picks the one matching the document's currency, else your default.
          </div>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={add}
                 className="h-8 rounded-none" data-testid="bp-bank-add">
          <Plus className="w-3 h-3 mr-1" /> Add account
        </Button>
      </div>

      {rows.length === 0 && (
        <div className="rounded border border-dashed border-border p-4 text-center text-xs text-muted-foreground"
              data-testid="bp-bank-empty">
          No accounts yet. Add one for every currency you invoice in — INR for the home workspace, and USD / EUR / etc. for international payments.
        </div>
      )}

      {rows.map((r, i) => {
        const isIntl = (r.currency || "").toUpperCase() !== "INR";
        return (
          <div key={i} className="border border-border rounded-md p-3 bg-background"
                data-testid={`bp-bank-row-${i}`}>
            <div className="flex items-center gap-2 mb-2">
              <Input
                value={r.label || ""}
                onChange={(e) => update(i, { label: e.target.value })}
                placeholder="Label e.g. INR primary / USD Mercury"
                className="h-9 flex-1 rounded-none font-medium"
                data-testid={`bp-bank-label-${i}`}
              />
              <Input
                value={r.currency || ""}
                onChange={(e) => update(i, { currency: e.target.value.toUpperCase() })}
                placeholder="INR"
                maxLength={6}
                className="h-9 w-20 rounded-none font-mono"
                data-testid={`bp-bank-currency-${i}`}
              />
              <button type="button"
                       onClick={() => setDefault(i)}
                       title={r.is_default ? "Default account" : "Make default"}
                       className={"h-9 w-9 flex items-center justify-center rounded border " +
                         (r.is_default
                           ? "border-amber-400 bg-amber-50 text-amber-600"
                           : "border-border text-muted-foreground hover:text-foreground")}
                       data-testid={`bp-bank-default-${i}`}>
                <Star className={"w-4 h-4 " + (r.is_default ? "fill-current" : "")} />
              </button>
              <button type="button"
                       onClick={() => remove(i)}
                       title="Remove"
                       className="h-9 w-9 flex items-center justify-center rounded border border-border text-rose-500 hover:bg-rose-50"
                       data-testid={`bp-bank-remove-${i}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input value={r.bank_name || ""}
                      onChange={(e) => update(i, { bank_name: e.target.value })}
                      placeholder="Bank name"
                      className="h-9 rounded-none"
                      data-testid={`bp-bank-name-${i}`} />
              <Input value={r.account_name || ""}
                      onChange={(e) => update(i, { account_name: e.target.value })}
                      placeholder="Account holder name"
                      className="h-9 rounded-none"
                      data-testid={`bp-bank-account-name-${i}`} />
              <Input value={r.account_number || ""}
                      onChange={(e) => update(i, { account_number: e.target.value })}
                      placeholder="Account number"
                      className="h-9 rounded-none"
                      data-testid={`bp-bank-account-number-${i}`} />
              <Input value={r.branch || ""}
                      onChange={(e) => update(i, { branch: e.target.value })}
                      placeholder="Branch"
                      className="h-9 rounded-none"
                      data-testid={`bp-bank-branch-${i}`} />
              {!isIntl && (
                <Input value={r.ifsc || ""}
                        onChange={(e) => update(i, { ifsc: e.target.value.toUpperCase() })}
                        placeholder="IFSC"
                        className="h-9 rounded-none font-mono"
                        data-testid={`bp-bank-ifsc-${i}`} />
              )}
              {isIntl && (
                <Input value={r.swift || ""}
                        onChange={(e) => update(i, { swift: e.target.value.toUpperCase() })}
                        placeholder="SWIFT / BIC"
                        className="h-9 rounded-none font-mono"
                        data-testid={`bp-bank-swift-${i}`} />
              )}
              {isIntl && (
                <Input value={r.iban || ""}
                        onChange={(e) => update(i, { iban: e.target.value.toUpperCase() })}
                        placeholder="IBAN (optional)"
                        className="h-9 rounded-none font-mono sm:col-span-2"
                        data-testid={`bp-bank-iban-${i}`} />
              )}
              <Input value={r.upi_id || ""}
                      onChange={(e) => update(i, { upi_id: e.target.value })}
                      placeholder={isIntl ? "— (UPI not applicable)" : "UPI ID"}
                      disabled={isIntl}
                      className="h-9 rounded-none sm:col-span-2"
                      data-testid={`bp-bank-upi-${i}`} />
              <Textarea value={r.bank_address || ""}
                         onChange={(e) => update(i, { bank_address: e.target.value })}
                         placeholder={isIntl ? "Bank address (required by some wire transfers)" : "Bank address (optional)"}
                         rows={2}
                         className="rounded-none sm:col-span-2"
                         data-testid={`bp-bank-address-${i}`} />

              {/* Feb 2026 R293zze · International wire fields */}
              {isIntl && (
                <>
                  <Input value={r.payment_method || ""}
                          onChange={(e) => update(i, { payment_method: e.target.value })}
                          placeholder="Payment method · e.g. ACH or Fedwire"
                          className="h-9 rounded-none sm:col-span-2"
                          data-testid={`bp-bank-payment-method-${i}`} />
                  <Input value={r.ach_routing || ""}
                          onChange={(e) => update(i, { ach_routing: e.target.value })}
                          placeholder="ACH routing number"
                          className="h-9 rounded-none font-mono"
                          data-testid={`bp-bank-ach-${i}`} />
                  <Input value={r.fedwire_routing || ""}
                          onChange={(e) => update(i, { fedwire_routing: e.target.value })}
                          placeholder="Fedwire routing number"
                          className="h-9 rounded-none font-mono"
                          data-testid={`bp-bank-fedwire-${i}`} />
                  <Textarea value={r.beneficiary_address || ""}
                             onChange={(e) => update(i, { beneficiary_address: e.target.value })}
                             placeholder="Beneficiary address · account holder's legal address, e.g. 5 Penn Plaza, 14th Floor, New York, NY 10001, US"
                             rows={2}
                             className="rounded-none sm:col-span-2"
                             data-testid={`bp-bank-beneficiary-${i}`} />
                </>
              )}

              {/* Feb 2026 R293zze · Default for currencies — one account
                  can be the explicit default for multiple ISO codes
                  (e.g. a Mercury USD account also serving CAD). */}
              <div className="sm:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                  Default for currencies
                </div>
                <Input value={(r.default_for_currency || []).join(", ")}
                        onChange={(e) => {
                          const list = e.target.value
                            .split(",").map((s) => s.trim().toUpperCase()).filter(Boolean);
                          update(i, { default_for_currency: list });
                        }}
                        placeholder="USD, CAD, EUR · comma-separated ISO codes"
                        className="h-9 rounded-none font-mono"
                        data-testid={`bp-bank-default-currencies-${i}`} />
                <div className="text-[10px] text-muted-foreground mt-1">
                  When a new {r.currency ? `${r.currency} ` : ""}invoice is in one of these currencies, this account is auto-picked (even over the star-default).
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

